import React, { useState, useMemo } from 'react';
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar"; // Assuming this adapts to theme
import { CalendarIcon, ChevronDown, ChevronUp, Download, Trash2, RefreshCw, InfoIcon, Archive, Image as ImageIcon, FileText, ExternalLink } from 'lucide-react'; // Added ImageIcon, FileText, ExternalLink
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog"; // Assuming these adapt
import { Switch } from "../ui/switch"; // Assuming this adapts
import { Label } from "../ui/label";
import { expenseService } from '../../services/expenseService';
import { getAuth } from 'firebase/auth';
import { useToast } from "../../hooks/use-toast";
import { useTheme } from '../ui/theme-provider'; // Import useTheme
import { cn } from "../../lib/utils"; // Import cn

const ExpensesList = ({ expenses, refreshExpenses }) => {
  const [dateRange, setDateRange] = useState('thisMonth');
  const [customDateRange, setCustomDateRange] = useState({
    from: new Date(new Date().getFullYear(), 0, 1), // Jan 1st of current year
    to: new Date()
  });
  const [showDeleted, setShowDeleted] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [expandedExpense, setExpandedExpense] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const { theme } = useTheme(); // Get theme
  const { toast } = useToast();
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [archiveDateRange, setArchiveDateRange] = useState({
    from: new Date(new Date().getFullYear() - 3, 0, 1), // 3 years ago
    to: new Date()
  });
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [viewImageModalOpen, setViewImageModalOpen] = useState(false);
  const [viewImageUrl, setViewImageUrl] = useState('');
  
  const auth = getAuth();
  
  // Categories for filtering - Updated labels
  const categories = [
    { id: 'all', name: 'All Categories' },
    { id: 'travel', name: 'Travel & Mileage' },
    { id: 'parking', name: 'Parking Fees' },
    { id: 'subsistence', name: 'Food & Drink (Subsistence)' },
    { id: 'equipment', name: 'Equipment & Instruments' },
    { id: 'materials', name: 'Clinical Materials & Consumables' }, // Added materials
    { id: 'course', name: 'Courses & CPD Training' }, // Added course
    { id: 'insurance', name: 'Indemnity & Insurance' }, // Added insurance
    { id: 'membership', name: 'GDC Fees & Memberships' }, // Added membership
    { id: 'telephone', name: 'Phone & Internet' }, // Added telephone
    { id: 'software', name: 'Software & Subscriptions' }, // Added software (renamed from subscription)
    { id: 'other', name: 'Other Business Expenses' }
    // Removed older/less specific categories like 'subscription', 'officeSupplies', 'professional' if they map to the new ones
  ];
  
  const getDateRangeBoundaries = () => {
    const now = new Date();
    let start, end;
    
    switch (dateRange) {
      case 'today':
        // Start and end are both today
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'yesterday':
        // Yesterday
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
        break;
      case 'thisWeek':
        // This week (Sunday to today)
        const day = now.getDay(); // 0 = Sunday, 6 = Saturday
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
        end = new Date();
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
      case 'thisYear':
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
        break;
      case 'thisTaxYear':
        // UK tax year runs April 6 to April 5 next year
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth(); // 0-indexed
        const currentDay = now.getDate();
        
        // If we're on/after April 6th, current tax year started this calendar year
        // If we're before April 6th, current tax year started last calendar year
        if (currentMonth > 3 || (currentMonth === 3 && currentDay >= 6)) {
          // Current tax year: April 6 this year to April 5 next year
          start = new Date(currentYear, 3, 6, 0, 0, 0, 0); // April 6 this year
          end = new Date(currentYear + 1, 3, 5, 23, 59, 59, 999); // April 5 next year
        } else {
          // Current tax year: April 6 last year to April 5 this year
          start = new Date(currentYear - 1, 3, 6, 0, 0, 0, 0); // April 6 last year
          end = new Date(currentYear, 3, 5, 23, 59, 59, 999); // April 5 this year
        }
        break;
      case 'lastTaxYear':
        // Last tax year calculation
        const currentYearForLast = now.getFullYear();
        const currentMonthForLast = now.getMonth(); // 0-indexed
        const currentDayForLast = now.getDate();
        
        // If we're on/after April 6th, last tax year was previous calendar year to this calendar year
        // If we're before April 6th, last tax year was 2 calendar years ago to last calendar year
        if (currentMonthForLast > 3 || (currentMonthForLast === 3 && currentDayForLast >= 6)) {
          // Last tax year: April 6 last year to April 5 this year
          start = new Date(currentYearForLast - 1, 3, 6, 0, 0, 0, 0); // April 6 last year
          end = new Date(currentYearForLast, 3, 5, 23, 59, 59, 999); // April 5 this year
        } else {
          // Last tax year: April 6 two years ago to April 5 last year
          start = new Date(currentYearForLast - 2, 3, 6, 0, 0, 0, 0); // April 6 two years ago
          end = new Date(currentYearForLast - 1, 3, 5, 23, 59, 59, 999); // April 5 last year
        }
        break;
      case 'custom':
        start = customDateRange.from;
        end = new Date(
          customDateRange.to.getFullYear(),
          customDateRange.to.getMonth(),
          customDateRange.to.getDate(),
          23, 59, 59
        );
        break;
      case 'last3Months':
        start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
        end = new Date();
        break;
      case 'last6Months':
        start = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        end = new Date();
        break;
      case 'last12Months':
        start = new Date(now.getFullYear() - 1, now.getMonth(), 1);
        end = new Date();
        break;
      case 'allTime':
        // For all time, set a really early start date
        start = new Date(1970, 0, 1);
        end = new Date();
        break;
      default:
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date();
        break;
    }
    
    console.log(`Date range "${dateRange}":`, {
      start: start.toISOString(),
      end: end.toISOString(),
      startLocal: start.toString(),
      endLocal: end.toString()
    });
    
    return { start, end };
  };
  
  // FIXED: Memoize the filtered expenses properly
  const filteredExpenses = useMemo(() => {
    const { start, end } = getDateRangeBoundaries();
    
    console.log('Filtering expenses with:', {
      dateRange,
      start: start.toISOString(),
      end: end.toISOString(),
      filterCategory,
      showDeleted,
      totalExpenses: expenses.length
    });
    
    const filtered = [...expenses].filter(expense => {
      // Date filtering
      const expenseDate = new Date(expense.date);
      const dateMatch = expenseDate >= start && expenseDate <= end;
      
      // Category filtering
      const categoryMatch = filterCategory === 'all' || expense.category === filterCategory;
      
      // Status filtering for deleted items
      const statusMatch = showDeleted || expense.status !== 'deleted';
      
      const shouldInclude = dateMatch && categoryMatch && statusMatch;
      
      if (!shouldInclude) {
        console.log('Excluding expense:', {
          id: expense.id,
          date: expenseDate.toISOString(),
          category: expense.category,
          status: expense.status,
          dateMatch,
          categoryMatch,
          statusMatch
        });
      }
      
      return shouldInclude;
    });
    
    console.log(`Filtered from ${expenses.length} to ${filtered.length} expenses`);
    return filtered;
  }, [expenses, dateRange, customDateRange, showDeleted, filterCategory]);

  // FIXED: Calculate total from filtered expenses, not from getFilteredExpenses function
  const totalAmount = useMemo(() => {
    const total = filteredExpenses.reduce((sum, expense) => {
      const amount = parseFloat(expense.totalAmount) || 0;
      return sum + amount;
    }, 0);
    
    console.log('Calculated total amount:', total, 'from', filteredExpenses.length, 'expenses');
    return total;
  }, [filteredExpenses]);

  const handleConfirmDelete = async (id) => {
    if (!id) return;
    
    setLoading(true);
    try {
      // Find the expense to mark as deleted
      const expense = expenses.find(e => e.id === id);
      
      if (expense) {
        // Instead of deleting, update the expense with status "deleted"
        await expenseService.updateExpense(id, {
          ...expense,
          status: 'deleted',
          deletedAt: new Date()
        });
        
        // Create a modified copy of the expenses array with the item marked as deleted
        const updatedExpenses = expenses.map(exp => {
          if (exp.id === id) {
            return { ...exp, status: 'deleted', deletedAt: new Date() };
          }
          return exp;
        });
        
        // Update the UI directly with our local copy that has the item marked as deleted
        // This ensures the UI updates immediately
        setExpandedExpense(null); // Close any expanded items
        
        toast({
          title: "Expense Deleted",
          description: "Expense has been moved to deleted items",
          variant: "default",
        });
        
        // Just notify the parent component that the data was updated
        // We're passing the updatedExpenses array directly to avoid a full refresh
        refreshExpenses({ action: 'update', data: updatedExpenses });
      } else {
        console.error('Could not find expense with id:', id);
      }
      
      setConfirmDelete(null);
    } catch (error) {
      console.error('Error marking expense as deleted:', error);
      toast({
        title: "Error",
        description: "Failed to delete expense. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleConfirmRestore = async (id) => {
    if (!id) return;
    
    setLoading(true);
    try {
      // Find the expense to restore
      const expense = expenses.find(e => e.id === id);
      
      if (expense) {
        // Create a new object WITHOUT status and deletedAt fields
        // We don't send these fields to updateExpense
        const { status, deletedAt, ...restoredData } = expense;
        
        // Just send the data without status/deletedAt 
        // The service will handle removing these fields from Firestore
        await expenseService.updateExpense(id, restoredData);
        
        // Create a modified copy of the expenses array with the item restored
        const updatedExpenses = expenses.map(exp => {
          if (exp.id === id) {
            // Create a new object without status and deletedAt
            const { status, deletedAt, ...restored } = exp;
            return restored;
          }
          return exp;
        });
        
        // Update the UI directly with our local copy that has the item restored
        // This ensures the UI updates immediately
        setExpandedExpense(null); // Close any expanded items
        
        toast({
          title: "Expense Restored",
          description: "The expense has been successfully restored",
          variant: "success",
        });
        
        // Just notify the parent component that the data was updated
        // We're passing the updatedExpenses array directly to avoid a full refresh
        refreshExpenses({ action: 'update', data: updatedExpenses });
      } else {
        console.error('Could not find expense with id:', id);
        toast({
          title: "Error",
          description: "Could not find the expense to restore",
          variant: "destructive",
        });
      }
      
      // Close the confirm dialog
      setConfirmRestore(null);
    } catch (error) {
      console.error('Error restoring expense:', error);
      toast({
        title: "Error",
        description: "Failed to restore expense. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleExportCSV = () => {
    const expensesToExport = filteredExpenses; // Renamed for clarity
    if (expensesToExport.length === 0) return;
    
    const headers = [
      'Date', 'Category', 'Description', 'Amount', 'Type', 'Receipt Attached' // Added 'Receipt Attached'
    ].join(',');
    
    const csvRows = expensesToExport.map(expense => {
      const date = format(new Date(expense.date), 'yyyy-MM-dd');
      const category = categories.find(c => c.id === expense.category)?.name || expense.category || 'n/a';
      // Escape commas in description
      const description = expense.description ? `"${expense.description.replace(/"/g, '""')}"` : '';
      const amount = expense.totalAmount.toFixed(2);
      const type = expense.mileage ? 'Mileage' : 'Regular';
      const receiptAttached = expense.receiptUrl ? 'Yes' : 'No'; // Check if receiptUrl exists
      
      return [date, category, description, amount, type, receiptAttached].join(',');
    });
    
    const csvContent = [headers, ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    // Create download link and click it
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `expenses_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const formatCurrency = (amount) => {
    return `£${parseFloat(amount).toFixed(2)}`;
  };
  
  const formatExpenseType = (expense) => {
    if (expense.category === 'travel') {
      if (expense.mileage) {
        return `${expense.mileage} miles @ ${expense.mileageRate}p/mile`;
      } else {
        // Find the user-friendly name for travel if not mileage
        const categoryInfo = categories.find(c => c.id === 'travel');
        return categoryInfo ? categoryInfo.name : 'Travel'; // Fallback
      }
    }
    // Find user-friendly name for other categories
    const categoryInfo = categories.find(c => c.id === expense.category);
    return categoryInfo ? categoryInfo.name : expense.category; // Fallback to ID if not found
  };
  
  const fetchArchiveData = async () => {
    if (!archiveDateRange.from || !archiveDateRange.to) {
      toast({
        title: "Invalid Date Range",
        description: "Please select both start and end dates",
        variant: "destructive",
      });
      return;
    }
    
    setArchiveLoading(true);
    try {
      const auth = getAuth();
      if (!auth.currentUser) return;
      
      // Use the date range specific function instead of the general fetch
      const archivedExpenses = await expenseService.getExpensesByDateRange(
        auth.currentUser.uid,
        archiveDateRange.from,
        archiveDateRange.to
      );
      
      // Close dialog
      setShowArchiveDialog(false);
      
      if (archivedExpenses.length === 0) {
        toast({
          title: "No Data Found",
          description: "No expenses found for the selected date range",
          variant: "default",
        });
        return;
      }
      
      // Refresh with archived data
      await refreshExpenses(archivedExpenses);
      
      // Set filter to show all data regardless of age
      setDateRange('allTime');
      
      toast({
        title: "Archive Data Loaded",
        description: `Loaded ${archivedExpenses.length} expenses from archive. Showing all data.`,
        variant: "success",
      });
    } catch (error) {
      console.error('Error fetching archive data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch archived expenses. Please try again.",
        variant: "destructive",
      });
    } finally {
      setArchiveLoading(false);
    }
  };
  
  return (
    <>
      {/* Card: Use theme-aware background and border */}
      <Card className={cn("mb-6", theme === 'light' ? 'bg-white' : 'app-card-bg', 'border-border')}>
        <CardContent className="p-3 sm:p-6">
          <div className="flex flex-col mb-4">
            <div className="flex justify-between items-center mb-3">
              {/* Title: Use theme-aware text color */}
              <h2 className={cn("text-lg sm:text-xl font-bold", theme === 'light' ? 'text-black' : 'text-accent-foreground')}>Expense History</h2>
              <Button 
                onClick={handleExportCSV}
                // Keep green, ensure text contrast
                className="bg-green-600 hover:bg-green-700 text-white h-8 sm:h-9 text-xs sm:text-sm px-2 sm:px-4"
                disabled={filteredExpenses.length === 0}
              >
                <Download size={16} className="mr-1 sm:mr-2" />
                <span className="whitespace-nowrap">Export</span>
              </Button>
            </div>
            
            {/* Info Box: Use theme-aware background, border, and text */}
            <div className={cn(
              "border rounded-md p-2 mb-3 text-xs flex items-center",
              theme === 'light' 
                ? 'bg-blue-50 border-blue-200 text-blue-800' 
                : 'app-bg-secondary border-border app-text-secondary' 
            )}>
              {/* Icon: Use theme-aware color */}
              <InfoIcon size={14} className={cn(
                "mr-1.5 flex-shrink-0",
                theme === 'light' ? 'text-blue-600' : 'text-accent-foreground'
              )} />
              <span>Showing data from last 24 months.</span>
              <button 
                onClick={() => setShowArchiveDialog(true)}
                // Link: Use theme-aware color and hover
                className={cn(
                  "underline ml-auto flex items-center",
                  theme === 'light' 
                    ? 'text-blue-700 hover:text-blue-900' 
                    : 'text-accent-foreground hover:text-purple-400'
                )}
              >
                <Archive size={12} className="mr-1" />
                <span>Access older records</span>
              </button>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Period Select */}
              <Select value={dateRange} onValueChange={setDateRange}>
                {/* Trigger: Use theme-aware styles */}
                <SelectTrigger className={cn(
                  "w-full sm:w-48 text-sm h-9",
                  theme === 'light' 
                    ? 'bg-white border-gray-300 text-black' 
                    : 'app-input-bg app-input-border app-text-primary'
                )}>
                  <SelectValue placeholder="Select Period" />
                </SelectTrigger>
                {/* Content: Use theme-aware styles */}
                <SelectContent className={cn(
                  theme === 'light' ? 'bg-white border-gray-300' : 'app-card-bg app-card-border'
                )}>
                  {/* Items: Use theme-aware styles */}
                  <SelectItem value="thisMonth" className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-primary hover:bg-accent')}>This Month</SelectItem>
                  <SelectItem value="lastMonth" className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-primary hover:bg-accent')}>Last Month</SelectItem>
                  <SelectItem value="last3Months" className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-primary hover:bg-accent')}>Last 3 Months</SelectItem>
                  <SelectItem value="last6Months" className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-primary hover:bg-accent')}>Last 6 Months</SelectItem>
                  {/* Add other items with conditional styling */}
                  <SelectItem value="thisTaxYear" className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-primary hover:bg-accent')}>This Tax Year</SelectItem>
                  <SelectItem value="lastTaxYear" className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-primary hover:bg-accent')}>Last Tax Year</SelectItem>
                  <SelectItem value="allTime" className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-primary hover:bg-accent')}>All Time</SelectItem>
                </SelectContent>
              </Select>

              {/* Category Select */}
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                {/* Trigger: Use theme-aware styles */}
                <SelectTrigger className={cn(
                  "w-full sm:w-48 text-sm h-9",
                  theme === 'light' 
                    ? 'bg-white border-gray-300 text-black' 
                    : 'app-input-bg app-input-border app-text-primary'
                )}>
                  <SelectValue placeholder="Filter by Category" />
                </SelectTrigger>
                {/* Content: Use theme-aware styles */}
                <SelectContent className={cn(
                  theme === 'light' ? 'bg-white border-gray-300' : 'app-card-bg app-card-border'
                )}>
                  {/* Items: Use theme-aware styles */}
                  <SelectItem value="all" className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-primary hover:bg-accent')}>All Categories</SelectItem>
                  {categories.map(category => (
                    <SelectItem 
                      key={category.id} 
                      value={category.id}
                      className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-primary hover:bg-accent')}
                    >
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Total and Show Deleted Toggle */}
          <div className="mb-4 flex flex-row justify-between items-center border-b border-border pb-3 sm:pb-4">
            {/* Total Text: Use memoized totalAmount */}
            <div className={cn(
              "text-base sm:text-lg font-medium",
              theme === 'light' ? 'text-gray-700' : 'text-purple-300'
            )}>
              Total: <span className={cn("font-bold", theme === 'light' ? 'text-black' : 'text-white')}>{formatCurrency(totalAmount)}</span>
              <span className={cn(
                "text-sm ml-2",
                theme === 'light' ? 'text-gray-500' : 'text-gray-400'
              )}>
                ({filteredExpenses.length} expense{filteredExpenses.length !== 1 ? 's' : ''})
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Label: Use theme-aware color */}
              <Label 
                htmlFor="show-deleted"
                className={cn(
                  "text-xs sm:text-sm cursor-pointer select-none whitespace-nowrap",
                  theme === 'light' ? 'text-gray-600' : 'text-purple-300'
                )}
              >
                Show Deleted
              </Label>
              {/* Switch: Assuming it adapts via global styles */}
              <Switch
                id="show-deleted"
                checked={showDeleted}
                onCheckedChange={setShowDeleted}
              />
            </div>
          </div>
          
          {/* Expense List Items */}
          {/* Call getFilteredExpenses() here */}
          {filteredExpenses.length > 0 ? (
            <div className="space-y-2 sm:space-y-3 overflow-x-auto">
              {filteredExpenses.map((expense) => (
                <div 
                  key={expense.id} 
                  // Item Container: Theme-aware background and border
                  className={cn(
                    "rounded-lg border transition-all",
                    theme === 'light' 
                      ? 'bg-purple-50 hover:bg-purple-100' // Default light purple, darker on hover
                      : 'bg-[#1F1F2E] hover:border-purple-500/40', // Dark mode background
                    expense.status === 'deleted' 
                      ? (theme === 'light' ? 'border-red-300 bg-red-50/50 hover:bg-red-100/50' : 'border-red-500/30') // Deleted border/bg (added light hover)
                      : (theme === 'light' ? 'border-gray-200 hover:border-gray-300' : 'border-purple-500/20') // Normal border
                  )}
                >
                  <div 
                    className="flex flex-col p-3 sm:p-4 cursor-pointer"
                    onClick={() => setExpandedExpense(expandedExpense === expense.id ? null : expense.id)}
                  >
                    <div className="flex justify-between items-start w-full mb-1">
                      {/* Description: Theme-aware text color */}
                      <span className={cn(
                        "font-medium text-sm sm:text-base truncate max-w-[70%]",
                        expense.status === 'deleted' 
                          ? (theme === 'light' ? 'text-red-600' : 'text-red-400') 
                          : (theme === 'light' ? 'text-gray-900' : 'text-white')
                      )}>
                        {expense.description}
                      </span>
                      {/* Date: Theme-aware text color */}
                      <span className={cn(
                        "text-xs sm:text-sm whitespace-nowrap text-right",
                        theme === 'light' ? 'text-gray-500' : 'text-purple-300'
                      )}>
                        {format(new Date(expense.date), 'dd MMM yyyy')}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-end w-full">
                      {/* Category/Type: Theme-aware text color */}
                      <div className={cn(
                        "text-xs sm:text-sm truncate max-w-[70%]",
                        theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                      )}>
                        {formatExpenseType(expense)}
                      </div>
                      
                      <div className="flex items-center gap-2 sm:gap-4">
                        {/* Amount: Theme-aware text color */}
                        <div className={cn(
                          "font-bold text-sm sm:text-base whitespace-nowrap",
                          expense.status === 'deleted' 
                            ? (theme === 'light' ? 'text-red-600' : 'text-red-400') 
                            : (theme === 'light' ? 'text-gray-900' : 'text-white')
                        )}>
                          {formatCurrency(expense.totalAmount)}
                        </div>
                        {/* Chevron: Theme-aware text color */}
                        {expandedExpense === expense.id ? (
                          <ChevronUp size={16} className={cn("flex-shrink-0", theme === 'light' ? 'text-gray-500' : 'text-purple-300')} />
                        ) : (
                          <ChevronDown size={16} className={cn("flex-shrink-0", theme === 'light' ? 'text-gray-500' : 'text-purple-300')} />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedExpense === expense.id && (
                    // Use theme-aware border
                    <div className={cn("border-t p-3 sm:p-4", theme === 'light' ? 'border-gray-200' : 'border-purple-500/20')}>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                        <div>
                          {/* Label: Theme-aware text color */}
                          <div className={cn("text-xs", theme === 'light' ? 'text-gray-500' : 'text-purple-300')}>Category</div>
                          {/* Value: Theme-aware text color */}
                          <div className={cn("text-sm", theme === 'light' ? 'text-gray-900' : 'text-white')}>
                            {categories.find(c => c.id === expense.category)?.name || expense.category}
                          </div>
                        </div>
                        
                        {expense.mileage && (
                          <div>
                            {/* Label: Theme-aware text color */}
                            <div className={cn("text-xs", theme === 'light' ? 'text-gray-500' : 'text-purple-300')}>Mileage Details</div>
                            {/* Value: Theme-aware text color */}
                            <div className={cn("text-sm", theme === 'light' ? 'text-gray-900' : 'text-white')}>
                              {expense.mileage} miles at {expense.mileageRate}p/mile = 
                              £{((parseFloat(expense.mileage) * expense.mileageRate) / 100).toFixed(2)}
                            </div>
                          </div>
                        )}
                        
                        {parseFloat(expense.amount) > 0 && !expense.mileage && ( // Show amount only if not mileage
                          <div>
                            {/* Label: Theme-aware text color */}
                            <div className={cn("text-xs", theme === 'light' ? 'text-gray-500' : 'text-purple-300')}>Amount</div>
                            {/* Value: Theme-aware text color */}
                            <div className={cn("text-sm", theme === 'light' ? 'text-gray-900' : 'text-white')}>{formatCurrency(expense.amount)}</div>
                          </div>
                        )}

                        {/* Receipt Thumbnail/Link */}
                        {expense.receiptUrl && (
                          <div>
                            <div className={cn("text-xs", theme === 'light' ? 'text-gray-500' : 'text-purple-300')}>Receipt</div>
                            <div className="mt-1">
                              {expense.isPdf || expense.receiptUrl.toLowerCase().includes('.pdf') ? (
                                <a
                                  href={expense.receiptUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={cn(
                                    "flex items-center text-sm p-2 rounded-md transition-colors",
                                    theme === 'light'
                                      ? 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300'
                                      : 'app-bg-secondary hover:bg-purple-900/30 text-purple-300 border border-purple-500/30'
                                  )}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <FileText size={18} className="mr-2 flex-shrink-0" />
                                  <span className="truncate">View PDF Receipt</span>
                                  <ExternalLink size={14} className="ml-auto flex-shrink-0 opacity-70" />
                                </a>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setViewImageUrl(expense.receiptUrl);
                                    setViewImageModalOpen(true);
                                  }}
                                  className={cn(
                                    "relative w-20 h-20 rounded-md border overflow-hidden cursor-pointer group transition-all",
                                     theme === 'light' ? 'border-gray-300 hover:border-purple-400' : 'border-purple-500/30 hover:border-purple-500'
                                  )}
                                >
                                  <img
                                    src={expense.receiptUrl}
                                    alt="Receipt thumbnail"
                                    className="w-full h-full object-cover"
                                  />
                                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <ImageIcon size={24} className="text-white" />
                                  </div>
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-3 sm:mt-4 flex justify-end gap-2">
                        {expense.status === 'deleted' ? (
                          <Button 
                            variant="outline" 
                            size="sm"
                            // Restore Button: Theme-aware styles
                            className={cn(
                              "text-xs h-8",
                              theme === 'light' 
                                ? 'bg-green-100 text-green-700 hover:bg-green-200 border-green-300' 
                                : 'bg-green-500/10 text-green-400 hover:bg-green-500/20 border-green-500/30'
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmRestore(expense.id);
                            }}
                          >
                            <RefreshCw size={14} className="mr-1 sm:mr-2" />
                            Restore
                          </Button>
                        ) : (
                          <Button 
                            variant="outline" 
                            size="sm"
                            // Delete Button: Theme-aware styles
                            className={cn(
                              "text-xs h-8",
                              theme === 'light' 
                                ? 'bg-red-100 text-red-700 hover:bg-red-200 border-red-300' 
                                : 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border-red-500/30'
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDelete(expense.id);
                            }}
                          >
                            <Trash2 size={14} className="mr-1 sm:mr-2" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            // No Expenses Message: Theme-aware text color
            <div className={cn(
              "p-4 sm:p-8 text-center text-sm",
              theme === 'light' ? 'text-gray-500' : 'text-gray-400'
            )}>
              No expenses found for the selected filters.
              {dateRange !== 'allTime' && (
                <div className="mt-2 text-xs">
                  Try selecting "All Time" or adjusting your date range.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Dialogs: Assuming they adapt via global styles or have internal theme logic */}
      {/* Delete Confirm Dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        {/* Use theme-aware styles if DialogContent doesn't adapt */}
        <DialogContent className={cn(theme === 'light' ? 'bg-white' : 'bg-[#2A2A3E] border-purple-500/20')}>
          <DialogHeader>
            {/* Use theme-aware styles */}
            <DialogTitle className={cn(theme === 'light' ? 'text-black' : 'text-purple-300')}>Confirm Deletion</DialogTitle>
            <DialogDescription className={cn(theme === 'light' ? 'text-gray-600' : 'text-gray-300')}>
              Are you sure you want to delete this expense? You can restore it later if needed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            {/* Cancel Button: Theme-aware */}
            <Button 
              variant="outline" 
              onClick={() => setConfirmDelete(null)}
              className={cn(theme === 'light' ? 'text-black border-gray-300' : 'bg-transparent text-white')}
            >
              Cancel
            </Button>
            {/* Delete Button: Keep destructive variant */}
            <Button 
              variant="destructive" 
              onClick={() => handleConfirmDelete(confirmDelete)}
              disabled={loading}
              // Destructive variant should handle themes, or add explicit styles
            >
              {loading ? 'Deleting...' : 'Delete Expense'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Restore Confirm Dialog */}
      <Dialog open={!!confirmRestore} onOpenChange={(open) => !open && setConfirmRestore(null)}>
        {/* Use theme-aware styles if DialogContent doesn't adapt */}
        <DialogContent className={cn(theme === 'light' ? 'bg-white' : 'bg-[#2A2A3E] border-purple-500/20')}>
          <DialogHeader>
            {/* Use theme-aware styles */}
            <DialogTitle className={cn(theme === 'light' ? 'text-black' : 'text-purple-300')}>Confirm Restore</DialogTitle>
            <DialogDescription className={cn(theme === 'light' ? 'text-gray-600' : 'text-gray-300')}>
              Are you sure you want to restore this deleted expense?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            {/* Cancel Button: Theme-aware */}
            <Button 
              variant="outline" 
              onClick={() => setConfirmRestore(null)}
              className={cn(theme === 'light' ? 'text-black border-gray-300' : 'bg-transparent text-white')}
            >
              Cancel
            </Button>
            {/* Restore Button: Theme-aware */}
            <Button 
              onClick={() => handleConfirmRestore(confirmRestore)}
              disabled={loading}
              className={cn(
                theme === 'light' 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'bg-green-600 text-white hover:bg-green-700' // Keep green consistent
              )}
            >
              {loading ? 'Restoring...' : 'Restore Expense'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Archive Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        {/* Use theme-aware styles if DialogContent doesn't adapt */}
        <DialogContent className={cn(theme === 'light' ? 'bg-white' : 'bg-[#2A2A3E] border-purple-500/20')}>
          <DialogHeader>
            {/* Use theme-aware styles */}
            <DialogTitle className={cn(theme === 'light' ? 'text-black' : 'text-purple-300')}>Access Archived Expenses</DialogTitle>
            <DialogDescription className={cn(theme === 'light' ? 'text-gray-600' : 'text-gray-300')}>
              Select a date range to retrieve older expense records. Note that this may increase database usage.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 my-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                {/* Label: Theme-aware */}
                <Label className={cn("mb-1 block", theme === 'light' ? 'text-black' : 'text-purple-300')}>From Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    {/* Button: Theme-aware */}
                    <Button 
                      variant="outline" 
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        theme === 'light' 
                          ? 'bg-white border-gray-300 text-black' 
                          : 'bg-[#1F1F2E] border-purple-500/30 text-white'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {archiveDateRange.from ? format(archiveDateRange.from, 'PPP') : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  {/* Popover Content: Theme-aware */}
                  <PopoverContent className={cn(
                    "w-auto p-0",
                    theme === 'light' ? 'bg-white border-gray-300' : 'bg-[#2A2A3E] border-purple-500/30'
                  )}>
                    {/* Calendar: Assuming it adapts */}
                    <Calendar
                      mode="single"
                      selected={archiveDateRange.from}
                      onSelect={(date) => setArchiveDateRange(prev => ({ ...prev, from: date }))}
                      disabled={(date) => date > new Date() || (archiveDateRange.to && date > archiveDateRange.to)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div>
                {/* Label: Theme-aware */}
                <Label className={cn("mb-1 block", theme === 'light' ? 'text-black' : 'text-purple-300')}>To Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    {/* Button: Theme-aware */}
                    <Button 
                      variant="outline" 
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        theme === 'light' 
                          ? 'bg-white border-gray-300 text-black' 
                          : 'bg-[#1F1F2E] border-purple-500/30 text-white'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {archiveDateRange.to ? format(archiveDateRange.to, 'PPP') : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  {/* Popover Content: Theme-aware */}
                  <PopoverContent className={cn(
                    "w-auto p-0",
                    theme === 'light' ? 'bg-white border-gray-300' : 'bg-[#2A2A3E] border-purple-500/30'
                  )}>
                    {/* Calendar: Assuming it adapts */}
                    <Calendar
                      mode="single"
                      selected={archiveDateRange.to}
                      onSelect={(date) => setArchiveDateRange(prev => ({ ...prev, to: date }))}
                      disabled={(date) => date > new Date() || (archiveDateRange.from && date < archiveDateRange.from)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            {/* Cancel Button: Theme-aware */}
            <Button 
              variant="ghost" // Use ghost for less emphasis
              onClick={() => setShowArchiveDialog(false)}
              className={cn(theme === 'light' ? 'text-black' : 'text-white')}
            >
              Cancel
            </Button>
            {/* Retrieve Button: Theme-aware */}
            <Button
              onClick={fetchArchiveData}
              disabled={archiveLoading || !archiveDateRange.from || !archiveDateRange.to}
              className={cn(
                theme === 'light' 
                  ? 'bg-purple-600 text-white hover:bg-purple-700' // Keep purple consistent
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              )}
            >
              {archiveLoading ? 'Loading...' : 'Retrieve Data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Image Modal */}
      <Dialog open={viewImageModalOpen} onOpenChange={setViewImageModalOpen}>
        <DialogContent className={cn("max-w-3xl p-2", theme === 'light' ? 'bg-white' : 'bg-[#1F1F2E] border-purple-500/20')}>
          <DialogHeader className="sr-only">
            <DialogTitle>View Receipt Image</DialogTitle>
          </DialogHeader>
          {viewImageUrl && (
            <img src={viewImageUrl} alt="Full receipt" className="max-w-full max-h-[80vh] object-contain mx-auto rounded" />
          )}
          <DialogFooter className="mt-2 sm:justify-center">
            <div className="flex gap-2 justify-center">
              <Button 
                variant="outline" 
                onClick={async () => {
                  try {
                    // Fetch the file as a blob to force download
                    const response = await fetch(viewImageUrl);
                    const blob = await response.blob();
                    
                    // Create object URL from blob
                    const url = window.URL.createObjectURL(blob);
                    
                    // Create download link
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `receipt-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.jpg`;
                    
                    // Trigger download
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    
                    // Clean up object URL
                    window.URL.revokeObjectURL(url);
                    
                    toast({
                      title: "Download started",
                      description: "Receipt download has been initiated",
                      variant: "success",
                    });
                  } catch (error) {
                    console.error('Download failed:', error);
                    toast({
                      title: "Download failed", 
                      description: "Could not download the receipt. Please try again.",
                      variant: "destructive",
                    });
                  }
                }}
                className={cn(
                  "flex items-center gap-2",
                  theme === 'light' 
                    ? 'text-purple-700 border-purple-300 hover:bg-purple-50' 
                    : 'text-purple-300 border-purple-500/50 hover:bg-purple-900/30'
                )}
              >
                <Download size={16} />
                Download
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setViewImageModalOpen(false)} 
                className={cn(theme === 'light' ? 'text-black border-gray-300' : 'bg-transparent text-white')}
              >
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExpensesList;
